/**
 * This is the entry point for Feature Hub App integration
 */

import React from 'react';
import { FeatureAppDefinition, FeatureAppEnvironment, FeatureServices } from '@feature-hub/core';
import { ReactFeatureApp } from '@feature-hub/react';
import type { Logger } from '@feature-hub/logger';
import type { GfaLocaleServiceV1 } from '@volkswagen-onehub/gfa-locale-service';
import { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import { ThemeProvider } from '@oneaudi/unified-web-common';
// What's the difference between ContentService and ContentServiceV1
import type { ContentServiceV1 as ContentService } from '@volkswagen-onehub/audi-content-service';
import App from './FeatureApp';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'gfa:service-config-provider': GfaServiceConfigProviderV1;
  readonly 'gfa:locale-service': GfaLocaleServiceV1;
  readonly 'audi-content-service'?: ContentService;
}

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      'gfa:locale-service': '^1.0.0',
      'gfa:service-config-provider': '^1.0.0',
      'audi-tracking-service': '^2.1.0',
      'audi-content-service': '^1.0.0',
      'audi:envConfigService': '^1.0.0',
    },
    externals: {
      // react: '>= 16 < 18',
      react: '*',
      'styled-components': '*',
      '@feature-hub/react': '^2.7.0',
      'react-dom': '>= 16 < 18',
    },
  },
  optionalDependencies: {
    featureServices: {
      'audi-tracking-service': '^2.1.0',
      's2:logger': '^1.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
    },
  },

  create: ({ featureServices }: FeatureAppEnvironment<FeatureServiceDependencies, void>) => {
    const loggerService = featureServices['s2:logger'];
    loggerService?.info('Feature App created.');
    const contentService = featureServices['audi-content-service'];

    return {
      render: () => {
        return (
          <ThemeProvider>
            <App contentService={contentService} />
          </ThemeProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
